import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ChartTheme from "../classes/ChartTheme";
import ListWrapper from "../utils/ListWrapper";
import MathExt from "../classes/MathExt";
import NumberFormatter from "../classes/NumberFormatter";
import CanvasView from "./CanvasView";
import ChartThemeData from "../classes/ChartThemeData";
import ChartNumberFormat from "../classes/ChartNumberFormat";
import ChartData from "../classes/ChartData";
import { CanvasViewController } from "./CanvasView";

export interface Pie2DChartProps extends BaseUIProps {
  key?: string;
  data: Array<ChartData>;
  numberFormat?: ChartNumberFormat;
  theme?: ChartTheme;
  labelPadding?: ui.EdgeInsets;
  canvasPadding?: ui.EdgeInsets;
  _dataHash?: number;
}

class _Pie2DChartState extends ObservableComponent<Pie2DChartProps> {
  static defaultProps = {
    numberFormat: null,
    theme: ChartTheme.Fusion,
    labelPadding: null,
    canvasPadding: null,
    data: [],
  };
  canvasViewController: CanvasViewController = new CanvasViewController();
  lastDownAngle: number = 0.0;
  startAngle: number = 0.0;
  public constructor(props: Pie2DChartProps) {
    super(props);

    this.initState();
  }
  public get data(): Array<ChartData> {
    return this.props.data;
  }
  public get numberFormat(): ChartNumberFormat {
    return this.props.numberFormat;
  }
  public get theme(): ChartTheme {
    return this.props.theme;
  }
  public get labelPadding(): ui.EdgeInsets {
    return this.props.labelPadding;
  }
  public get canvasPadding(): ui.EdgeInsets {
    return this.props.canvasPadding;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.subscribeToList(this.data, "data");

    this.updateSyncCollProperty("data", this.props.data);

    this.updateSyncProperty("numberFormat", this.props.numberFormat);

    this.on(["canvasPadding"], this.rebuild);
  }
  public componentDidUpdate(prevProps: Pie2DChartProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.data !== this.props.data) {
      this.updateObservableColl("data", prevProps.data, this.props.data);

      this.fire("data", this);
    }

    if (prevProps.numberFormat !== this.props.numberFormat) {
      this.updateObservable(
        "numberFormat",
        prevProps.numberFormat,
        this.props.numberFormat
      );

      this.fire("numberFormat", this);
    }

    if (prevProps.theme !== this.props.theme) {
      this.fire("theme", this);
    }

    if (prevProps.labelPadding !== this.props.labelPadding) {
      this.fire("labelPadding", this);
    }

    if (prevProps.canvasPadding !== this.props.canvasPadding) {
      this.fire("canvasPadding", this);
    }
  }
  public setLastDownAngle(val: number): void {
    let isValChanged: boolean = this.lastDownAngle !== val;

    if (!isValChanged) {
      return;
    }

    this.lastDownAngle = val;

    this.fire("lastDownAngle", this);
  }
  public setStartAngle(val: number): void {
    let isValChanged: boolean = this.startAngle !== val;

    if (!isValChanged) {
      return;
    }

    this.startAngle = val;

    this.fire("startAngle", this);
  }
  public render(): ReactNode {
    return ui.Container({
      padding: this.canvasPadding,
      child: CanvasView({
        controller: this.canvasViewController,
        onRepaint: () => {
          this.onRepaint();
        },
        d3eRef: ui.LayoutAware((bounds, globalPos) => {
          this.onLayoutChanged(bounds, globalPos);
        }, null),
        onPointerDown: (event) => {
          this.onPointerDown(event);
        },
        onPointerMove: (event) => {
          this.onPointerMove(event);
        },
      }),
      className: ui.join(this.props.className, "Pie2DChart x6707 h v"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onRepaint = (): void => {
    this.doRepaint();
  };
  public doRepaint = (): void => {
    let themeData: ChartThemeData = ChartThemeData.getData(this.theme);

    let numFormat: ChartNumberFormat =
      this.numberFormat ?? new ChartNumberFormat();

    let canvas: ui.Canvas = this.canvasViewController.canvas;

    let size: ui.Size = this.canvasViewController.size;

    let defaultTextStyle: ui.TextStyle = this.canvasViewController.textStyle;

    let total: number = this.data
      .map((d) => d.doubleValue)
      .fold(0.0, (t, v) => t + v);

    let gapBetweenLabels: number = 1.0;

    let piePadding: number = 10.0;

    //  Layout Legends

    let canvasHeight: number = size.height - piePadding * 2;

    let canvasWidth: number = size.width - piePadding * 2;

    //  Circle should be 1/4th minimum

    let circleSize: number = Math.min(canvasHeight, canvasWidth) / 4.0;

    let count: number = this.data.length;

    let labelPainters: Array<ui.TextPainter> = [];

    let maxLabelWidth: number = 0.0;

    let maxLabelHeight: number = 0.0;

    //  Layout the X-Axis Labels

    for (let i: number = 0; i < this.data.length; i++) {
      // TextStyle textStyle = TextStyleExt.merge(defaultTextStyle, labelTextStyle??TextStyle(color: themeData.labelColor));

      let percent: number = (this.data[i].doubleValue * 100.0) / total;

      let formattedPercent: string = NumberFormatter.formatDouble(
        percent,
        numFormat,
        { percent: true }
      );

      let label: string = this.data[i].label + ", " + formattedPercent;

      let style: ui.TextStyle = new ui.TextStyle({
        color: themeData.labelColor,
      });

      let textSpan: ui.TextSpan = new ui.TextSpan({
        text: label,
        style: style,
      });

      let textPainter: ui.TextPainter = new ui.TextPainter({
        text: textSpan,
        textDirection: ui.TextDirection.ltr,
      });

      textPainter.layout(canvas, { minWidth: 0.0, maxWidth: size.width / 2 });

      let width: number = textPainter.width;

      let height: number = textPainter.height;

      labelPainters.add(textPainter);

      maxLabelWidth = Math.max(maxLabelWidth, width);

      maxLabelHeight = Math.max(maxLabelHeight, width);
    }

    let connectorWidth: number = 20.0;

    let labelVPadding: number = this.labelPadding?.top ?? 2.0;

    let labelHPadding: number = this.labelPadding?.left ?? 10.0;

    let extraSpaceAfterLabel: number =
      size.width / 2.0 -
      piePadding -
      connectorWidth -
      labelHPadding * 2 -
      maxLabelWidth;

    // Increase circle size if we have extra space after labels

    circleSize += Math.max(extraSpaceAfterLabel, 0.0);

    let radius: number = circleSize / 2.0;

    let center: ui.Offset = ui.OffsetExt.getOffset({
      dx: size.width / 2.0,
      dy: size.height / 2.0,
    });

    let angle: number = this.startAngle;

    let connectorPaint: ui.Paint = new ui.Paint();

    connectorPaint.color = themeData.labelColor;

    connectorPaint.style = ui.PaintingStyle.stroke;

    let leftLabelRects: Array<ui.Rect> = [];

    let rightLabelRects: Array<ui.Rect> = [];

    for (let i: number = 0; i < this.data.length; i++) {
      let share: number = this.data[i].doubleValue / total;

      let nextAngle: number = share * 2 * MathExt.pi;

      let arcPaint: ui.Paint = new ui.Paint();

      arcPaint.color = themeData.dataColor(i);

      arcPaint.style = ui.PaintingStyle.fill;

      let rect: ui.Rect = ui.Rect.fromLTWH(
        center.dx - radius,
        center.dy - radius,
        radius * 2,
        radius * 2
      );

      let midPointAngle: number = -angle - nextAngle / 2;

      let x: number = center.dx + radius * Math.cos(midPointAngle);

      let y: number = center.dy + radius * Math.sin(midPointAngle);

      let x1: number =
        center.dx + (radius + connectorWidth) * Math.cos(midPointAngle);

      let y1: number =
        center.dy + (radius + connectorWidth) * Math.sin(midPointAngle);

      let painter: ui.TextPainter = labelPainters[i];

      //  Decide which side it is left or right

      let midPoint: ui.Offset = ui.OffsetExt.getOffset({ dx: x, dy: y });

      if (midPoint.dx < center.dx) {
        //  Left

        painter.paint(
          canvas,
          ui.OffsetExt.getOffset({
            dx: x1 - painter.width - 10.0,
            dy: y1 - painter.height / 2,
          })
        );

        canvas.drawLine(
          ui.OffsetExt.getOffset({ dx: x1, dy: y1 }),
          ui.OffsetExt.getOffset({ dx: x1 - 5.0, dy: y1 }),
          connectorPaint
        );
      } else {
        //  Right

        painter.paint(
          canvas,
          ui.OffsetExt.getOffset({ dx: x1 + 10.0, dy: y1 - painter.height / 2 })
        );

        canvas.drawLine(
          ui.OffsetExt.getOffset({ dx: x1, dy: y1 }),
          ui.OffsetExt.getOffset({ dx: x1 + 5.0, dy: y1 }),
          connectorPaint
        );
      }

      canvas.drawLine(
        ui.OffsetExt.getOffset({ dx: x, dy: y }),
        ui.OffsetExt.getOffset({ dx: x1, dy: y1 }),
        connectorPaint
      );

      canvas.drawLine(
        center,
        ui.OffsetExt.getOffset({ dx: x, dy: y }),
        connectorPaint
      );

      canvas.drawArc(rect, -angle, -nextAngle, true, arcPaint);

      angle = angle + nextAngle;
    }
  };
  public onLayoutChanged = (bounds: ui.Rect, globalPos: ui.Offset): void => {
    // doRepaint();
  };
  public onPointerDown = (event: ui.PointerDownEvent): void => {
    let size: ui.Size = this.canvasViewController.size;

    let center: ui.Offset = ui.OffsetExt.getOffset({
      dx: size.width / 2.0,
      dy: size.height / 2.0,
    });

    let location: ui.Offset = event.localPosition;

    this.setLastDownAngle(
      this.startAngle +
        Math.atan2(center.dy - location.dy, center.dx - location.dy)
    );
  };
  public onPointerMove = (event: ui.PointerMoveEvent): void => {
    let size: ui.Size = this.canvasViewController.size;

    let center: ui.Offset = ui.OffsetExt.getOffset({
      dx: size.width / 2.0,
      dy: size.height / 2.0,
    });

    let location: ui.Offset = event.localPosition;

    let newAngle: number = Math.atan2(
      center.dy - location.dy,
      center.dx - location.dy
    );

    this.setStartAngle(this.lastDownAngle - newAngle);

    this.canvasViewController.repaint();
  };
}
export default function Pie2DChart(props: Pie2DChartProps) {
  return React.createElement(
    _Pie2DChartState,
    { ..._Pie2DChartState.defaultProps, ...props },
    ListWrapper.fromInput<ChartData>(props.data, "data")
  );
}
